/**
 * Graph Builder - Constructs dependency graph from scanned files
 * Pure module - no file system dependencies
 */

import type {
  ScannedFile,
  ScanResult,
  GraphNode,
  GraphEdge,
  Cluster,
  ExternalPackage,
  GraphMetrics,
  DependencyGraph
} from './types';

/**
 * Build a complete dependency graph from scanned files
 */
export function buildDependencyGraph(scanResult: ScanResult): DependencyGraph {
  const { files, errors } = scanResult;

  // Create lookup maps
  const fileByRelativePath = new Map<string, ScannedFile>();
  for (const file of files) {
    fileByRelativePath.set(file.relativePath, file);
  }

  // Build nodes
  const nodes: GraphNode[] = files.map((file) => ({
    id: file.relativePath,
    name: file.name,
    directory: file.directory,
    extension: file.extension,
    lineCount: file.lineCount,
    size: file.size,
    exports: file.exports,
    inDegree: 0,
    outDegree: 0,
    group: getFileGroup(file)
  }));

  // Create node lookup
  const nodeById = new Map<string, GraphNode>();
  for (const node of nodes) {
    nodeById.set(node.id, node);
  }

  // Build edges (links)
  const edges: GraphEdge[] = [];
  const edgeSet = new Set<string>();
  const externalPackages = new Map<string, ExternalPackage>();

  for (const file of files) {
    const sourceId = file.relativePath;

    for (const imp of file.imports) {
      // Check if external (resolved is null and source doesn't start with . or /)
      const isExternal = !imp.resolved &&
        !imp.source.startsWith('.') &&
        !imp.source.startsWith('/') &&
        !imp.source.startsWith('@/');

      if (isExternal) {
        // Track external packages
        const pkg = imp.source.split('/')[0];
        if (!externalPackages.has(pkg)) {
          externalPackages.set(pkg, { name: pkg, usedBy: [], count: 0 });
        }
        const pkgInfo = externalPackages.get(pkg)!;
        pkgInfo.usedBy.push(sourceId);
        pkgInfo.count++;
        continue;
      }

      if (imp.resolved) {
        const targetPath = imp.resolved;
        const edgeKey = `${sourceId}|${targetPath}`;

        if (!edgeSet.has(edgeKey) && nodeById.has(targetPath)) {
          edgeSet.add(edgeKey);
          edges.push({
            source: sourceId,
            target: targetPath,
            importType: imp.type,
            namedImports: imp.namedImports || [],
            line: imp.line
          });

          // Update degrees
          const sourceNode = nodeById.get(sourceId);
          const targetNode = nodeById.get(targetPath);
          if (sourceNode) sourceNode.outDegree++;
          if (targetNode) targetNode.inDegree++;
        }
      }
    }
  }

  // Calculate metrics
  const metrics = calculateMetrics(nodes, edges, externalPackages);

  // Identify clusters/directories
  const clusters = identifyClusters(nodes);

  // Find circular dependencies
  const circularDeps = findCircularDependencies(nodes, edges);

  return {
    nodes,
    edges,
    externalPackages: Array.from(externalPackages.values()),
    clusters,
    circularDeps,
    metrics
  };
}

/**
 * Determine file group based on extension and path
 */
function getFileGroup(file: ScannedFile): string {
  const ext = file.extension.toLowerCase();
  const relativePath = file.relativePath.toLowerCase();

  // Check path-based groups first
  if (relativePath.includes('component')) return 'component';
  if (relativePath.includes('hook') || file.name.startsWith('use')) return 'hook';
  if (relativePath.includes('util') || relativePath.includes('helper') || relativePath.includes('lib')) return 'utility';
  if (relativePath.includes('api') || relativePath.includes('service')) return 'api';
  if (relativePath.includes('page') || relativePath.includes('app/')) return 'page';
  if (relativePath.includes('layout')) return 'layout';
  if (relativePath.includes('type') || relativePath.includes('interface')) return 'types';
  if (relativePath.includes('config')) return 'config';
  if (relativePath.includes('context') || relativePath.includes('provider')) return 'context';
  if (relativePath.includes('store') || relativePath.includes('redux') || relativePath.includes('zustand')) return 'state';
  if (relativePath.includes('test') || relativePath.includes('spec')) return 'test';
  if (relativePath.includes('style') || ext === '.css' || ext === '.scss') return 'style';
  if (relativePath.includes('contract') || relativePath.includes('.sol')) return 'contract';

  // Extension-based fallback
  if (['.tsx', '.jsx'].includes(ext)) return 'component';
  if (['.ts', '.js'].includes(ext)) return 'module';
  if (['.css', '.scss', '.sass', '.less'].includes(ext)) return 'style';
  if (['.json'].includes(ext)) return 'config';
  if (['.md', '.mdx'].includes(ext)) return 'docs';

  return 'other';
}

/**
 * Calculate various metrics for the codebase
 */
function calculateMetrics(
  nodes: GraphNode[],
  edges: GraphEdge[],
  externalPackages: Map<string, ExternalPackage>
): GraphMetrics {
  const totalFiles = nodes.length;
  const totalLines = nodes.reduce((sum, n) => sum + n.lineCount, 0);
  const totalSize = nodes.reduce((sum, n) => sum + n.size, 0);
  const totalEdges = edges.length;

  // Files by group
  const filesByGroup: Record<string, number> = {};
  for (const node of nodes) {
    filesByGroup[node.group] = (filesByGroup[node.group] || 0) + 1;
  }

  // Files by extension
  const filesByExtension: Record<string, number> = {};
  for (const node of nodes) {
    filesByExtension[node.extension] = (filesByExtension[node.extension] || 0) + 1;
  }

  // Most connected files (hubs)
  const hubs = [...nodes]
    .sort((a, b) => (b.inDegree + b.outDegree) - (a.inDegree + a.outDegree))
    .slice(0, 20)
    .map(n => ({
      id: n.id,
      total: n.inDegree + n.outDegree
    }));

  // Most imported files
  const mostImported = [...nodes]
    .filter(n => n.inDegree > 0)
    .sort((a, b) => b.inDegree - a.inDegree)
    .slice(0, 15)
    .map(n => ({ id: n.id, count: n.inDegree }));

  // Files with most dependencies
  const mostDependencies = [...nodes]
    .filter(n => n.outDegree > 0)
    .sort((a, b) => b.outDegree - a.outDegree)
    .slice(0, 15)
    .map(n => ({ id: n.id, count: n.outDegree }));

  // Orphan files (no connections)
  const orphans = nodes
    .filter(n => n.inDegree === 0 && n.outDegree === 0)
    .map(n => n.id);

  // Most used external packages
  const topExternalPackages = [...externalPackages.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, 20)
    .map(p => ({ name: p.name, count: p.count }));

  // Largest files
  const largestFiles = [...nodes]
    .sort((a, b) => b.lineCount - a.lineCount)
    .slice(0, 15)
    .map(n => ({ id: n.id, lines: n.lineCount }));

  // Average metrics
  const avgImportsPerFile = totalFiles > 0 ? totalEdges / totalFiles : 0;
  const avgLinesPerFile = totalFiles > 0 ? totalLines / totalFiles : 0;

  return {
    totalFiles,
    totalLines,
    totalSize,
    totalEdges,
    filesByGroup,
    filesByExtension,
    hubs,
    mostImported,
    mostDependencies,
    orphans,
    topExternalPackages,
    largestFiles,
    avgImportsPerFile,
    avgLinesPerFile
  };
}

/**
 * Identify directory clusters
 */
function identifyClusters(nodes: GraphNode[]): Cluster[] {
  const clusters = new Map<string, Cluster>();

  for (const node of nodes) {
    const dir = node.directory || '.';
    if (!clusters.has(dir)) {
      clusters.set(dir, {
        name: dir,
        files: [],
        totalLines: 0,
        totalSize: 0
      });
    }
    const cluster = clusters.get(dir)!;
    cluster.files.push(node.id);
    cluster.totalLines += node.lineCount;
    cluster.totalSize += node.size;
  }

  return Array.from(clusters.values())
    .sort((a, b) => b.files.length - a.files.length);
}

/**
 * Find circular dependencies using DFS
 */
function findCircularDependencies(nodes: GraphNode[], edges: GraphEdge[]): string[][] {
  const graph = new Map<string, string[]>();

  // Build adjacency list
  for (const node of nodes) {
    graph.set(node.id, []);
  }
  for (const edge of edges) {
    if (graph.has(edge.source)) {
      graph.get(edge.source)!.push(edge.target);
    }
  }

  const cycles: string[][] = [];
  const visited = new Set<string>();
  const recursionStack = new Set<string>();
  const path: string[] = [];

  function dfs(nodeId: string) {
    visited.add(nodeId);
    recursionStack.add(nodeId);
    path.push(nodeId);

    const neighbors = graph.get(nodeId) || [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        dfs(neighbor);
      } else if (recursionStack.has(neighbor)) {
        // Found a cycle
        const cycleStart = path.indexOf(neighbor);
        if (cycleStart !== -1) {
          const cycle = path.slice(cycleStart);
          cycle.push(neighbor); // Complete the cycle
          cycles.push(cycle);
        }
      }
    }

    path.pop();
    recursionStack.delete(nodeId);
  }

  for (const node of nodes) {
    if (!visited.has(node.id)) {
      dfs(node.id);
    }
  }

  // Dedupe cycles (same cycle can be found starting from different nodes)
  const uniqueCycles: string[][] = [];
  const cycleSet = new Set<string>();

  for (const cycle of cycles) {
    const normalized = [...cycle.slice(0, -1)].sort().join('|');
    if (!cycleSet.has(normalized)) {
      cycleSet.add(normalized);
      uniqueCycles.push(cycle);
    }
  }

  return uniqueCycles.slice(0, 50); // Limit to 50 cycles
}

/**
 * Export graph data as JSON
 */
export function exportGraphAsJson(graph: DependencyGraph): string {
  return JSON.stringify(graph, null, 2);
}
